export const FIRST_PERIOD = {
  en: {
    seo: {
      title: "First Period & Menstruation Workshop for Schools in English | Rotterdam",
      description: "An English-language school workshop preparing girls aged 10–14 for menstruation and their first period with clear information, practical preparation and space for their real questions.",
    },
    hero: {
      eyebrow: "A first period doesn't need to arrive as something a girl is expected to already know how to handle",
      title: "First Periods & Growing Up",
      sub: "A two-session school workshop that prepares girls for their first period — calmly, honestly and without embarrassment.",
      intro: "Delivered at your school, in English, for girls approaching menarche or in early menstruation.",
      facts: ["2 × 75 minutes", "Ages 10–14", "At your school", "In English", "Schiedam · Rotterdam · The Hague"],
      primaryCta: "Enquire about a school workshop",
      secondaryCta: "How the workshop works",
    },
    school: {
      heading: "Two-session school workshop",
      rows: [
        "For girls aged 10–14, approaching menarche or in early menstruation",
        "Two sessions of 75 minutes, delivered at your school",
        "Groups of 15–25 girls · in English",
        "Schiedam · Rotterdam · The Hague + surrounding areas",
        "€550 all-inclusive",
      ],
      cta: "Enquire about a school workshop",
    },
    why2: {
      title: "Why two sessions?",
      p1: "A single lesson can inform. Two sessions can prepare.",
      p2: "In the first session, we explore what a first period is, what changes in the body, and what daily life with a period can look like — practically and honestly.",
      p3: "Between the sessions, there is time to digest. Questions surface at home, in the days afterwards, when a girl isn't sitting in a classroom.",
      p4: "In the second session, we return to those real questions together — the ones that only come up once the first information has settled.",
    },
    explore: {
      title: "What girls explore",
      items: [
        "What a period actually is — and what is happening in the body",
        "The cycle beyond the bleeding days",
        "Practicalities: products, being prepared, and handling a period at school",
        "Feelings: curiosity, nervousness, embarrassment and everything in between",
        "Questions girls rarely get to ask out loud",
      ],
    },
    how: {
      title: "How it works",
      p1: "I come to your school and work with the same group of girls across two sessions, in a format shaped together with the teacher or coordinator.",
      p2: "The atmosphere is calm and matter-of-fact. Nothing is forced to be celebrated, and nothing is treated as shameful.",
      pull: "Positive doesn't mean forcing celebration. Girls are offered an alternative to shame and dread — without anyone prescribing how they ought to feel.",
    },
    questions: {
      title: "The real questions girls carry",
      items: ["What if it happens at school?", "What if I leak?", "Can I still swim?", "Who do I tell?"],
      closing: "When these questions have honest answers, a first period becomes something a girl can meet prepared.",
    },
    takeHome: {
      title: "What they take home",
      p1: "A clearer picture of what is coming — and the knowledge that it can be talked about.",
      p2: "Practical confidence: what to do, what to carry, who to ask.",
      p3: "And the beginning of a different story about periods than the one most of us grew up with.",
    },
    irida: {
      heading: "About the facilitator",
      body: "I'm Irida Hysenbelli, founder of Women's Den. I hold workshops and circles for women and girls around the transitions of their lives — including first periods. I trained in Menstruality Leadership with Red School, where I also completed the Menarche Course (2023), and bring a background in education and anthropology. Schools can expect a well-prepared, professional and gentle presence in the classroom.",
      link: "More about Irida",
    },
    parents: {
      title: "For parents",
      p1: "Supporting your daughter towards her first period can raise questions at home too. This small guide is for you — for the first conversation, and everything around it.",
    },
    booklet: {
      badge: "Coming soon",
      availableBadge: "Now available",
      buyCta: "Order the booklet",
      title: "The First Period Booklet",
      p1: "A small printed booklet for girls approaching their first period is in preparation — something to keep, read slowly and return to.",
    },
    final: {
      pull: "A first period doesn't need to arrive as something a girl is expected to already know how to handle. We can prepare her.",
      title: "Prepare her — calmly, honestly, together.",
      rows: [
        "Two-session school workshop · 2 × 75 minutes",
        "For girls aged 10–14, approaching menarche or in early menstruation",
        "Groups of 15–25 girls · delivered at your school · in English",
        "Schiedam · Rotterdam · The Hague + surrounding areas",
        "€550 all-inclusive",
      ],
      cta: "Enquire about a school workshop",
      secondary: "A question first? Get in touch",
    },
  },
  nl: {
    seo: {
      title: "Engelstalige menstruatieworkshop voor scholen | Rotterdam",
      description: "Engelstalige menstruatievoorlichting voor scholen: een gastles in twee sessies die meisjes van 10–14 jaar voorbereidt op de eerste menstruatie, met heldere informatie, praktische voorbereiding en ruimte voor echte vragen. Voor scholen in Schiedam, Rotterdam, Den Haag en omgeving.",
    },
    hero: {
      eyebrow: "De eerste menstruatie hoeft niet aan te komen als iets dat een meisje al zou moeten beheersen",
      title: "Eerste menstruatie & opgroeien",
      sub: "Een schoolworkshop in twee sessies die meisjes voorbereidt op hun eerste menstruatie — rustig, eerlijk en zonder schaamte.",
      intro: "Gegeven op jullie school, in het Engels, voor meisjes die nog niet of pas sinds kort menstrueren.",
      facts: ["2 × 75 minuten", "10–14 jaar", "Op jullie school", "In het Engels", "Schiedam · Rotterdam · Den Haag"],
      primaryCta: "Vraag naar een schoolworkshop",
      secondaryCta: "Hoe de workshop werkt",
    },
    school: {
      heading: "Schoolworkshop in twee sessies",
      rows: [
        "Voor meisjes van 10–14 jaar die nog niet of pas sinds kort menstrueren",
        "Twee sessies van 75 minuten, gegeven op jullie school",
        "Groepen van 15–25 meisjes · in het Engels",
        "Schiedam · Rotterdam · Den Haag en omgeving",
        "€550 all-inclusive",
      ],
      cta: "Vraag naar een schoolworkshop",
    },
    why2: {
      title: "Waarom twee sessies?",
      p1: "Eén les kan informeren. Twee sessies geven tijd om je voor te bereiden.",
      p2: "In de eerste sessie verkennen we wat een eerste menstruatie betekent, wat er in het lichaam verandert en hoe het dagelijks leven met menstruatie eruit kan zien — praktisch en eerlijk.",
      p3: "Tussen de sessies is er tijd om alles even te laten bezinken. Vaak komen vragen pas later — thuis, of in de dagen erna, wanneer een meisje niet meer in het klaslokaal zit.",
      p4: "In de tweede sessie komen we terug op die vragen — de vragen die vaak pas ontstaan nadat er wat tijd overheen is gegaan.",
    },
    explore: {
      title: "Wat meisjes verkennen",
      items: [
        "Wat menstruatie eigenlijk is — en wat er in het lichaam gebeurt",
        "De menstruatiecyclus — meer dan alleen de dagen waarop je bloedt",
        "Praktische zaken: menstruatieproducten, voorbereid zijn en omgaan met menstruatie op school",
        "Gevoelens: nieuwsgierigheid, zenuwen, schaamte en alles daartussenin",
        "Vragen die meisjes niet altijd hardop durven te stellen",
      ],
    },
    how: {
      title: "Hoe het werkt",
      p1: "Ik kom naar jullie school en werk tijdens twee sessies met dezelfde groep meisjes, in een vorm die we samen met de leerkracht of coördinator afstemmen.",
      p2: "De sfeer is rustig en nuchter. Niets hoeft geforceerd gevierd te worden en niets wordt als beschamend behandeld.",
      pull: "Positief betekent niet dat we menstruatie geforceerd vieren. We maken ruimte voor een andere ervaring dan schaamte of angst — zonder meisjes voor te schrijven hoe ze zich zouden moeten voelen",
    },
    questions: {
      title: "De vragen die meisjes echt bezighouden",
      items: ["Wat als het op school gebeurt?", "Wat als ik doorlek?", "Kan ik nog steeds zwemmen?", "Wie vertel ik het?"],
      closing: "Als deze vragen eerlijk beantwoord worden, kan een meisje haar eerste menstruatie met meer vertrouwen tegemoetzien.",
    },
    takeHome: {
      title: "Wat ze mee naar huis nemen",
      p1: "Een duidelijker beeld van wat ze kunnen verwachten — en de wetenschap dat erover gepraat mag worden.",
      p2: "Praktische kennis: wat je kunt doen, wat handig is om bij je te hebben en bij wie je terechtkunt met vragen.",
      p3: "En het begin van een ander verhaal over menstruatie dan het verhaal waarmee velen van ons zijn opgegroeid.",
    },
    irida: {
      heading: "Over de begeleider",
      body: "Ik ben Irida Hysenbelli, oprichter van Women's Den. Ik begeleid workshops en groepen voor vrouwen en meisjes rond belangrijke overgangen in hun leven, waaronder de eerste menstruatie. Mijn achtergrond in onderwijs en antropologie, samen met mijn opleiding in Menstruality Leadership en Menarche bij Red School, vormt de basis voor hoe ik met dit onderwerp werk: helder en praktisch, met ruimte voor vragen en voor de verschillende manieren waarop meisjes deze verandering kunnen ervaren.",
      link: "Meer over Irida",
    },
    parents: {
      title: "Voor ouders",
      p1: "Je dochter begeleiden naar haar eerste menstruatie kan ook vragen oproepen thuis. Deze kleine gids is voor jou — voor het eerste gesprek, en alles eromheen.",
    },
    booklet: {
      badge: "Binnenkort",
      availableBadge: "Nu verkrijgbaar",
      buyCta: "Bestel het boekje",
      title: "Het Eerste Menstruatie-boekje",
      p1: "Ik werk aan een klein gedrukt boekje voor meisjes die nog niet of pas sinds kort menstrueren — iets om te bewaren, op je gemak te lezen en later nog eens bij te pakken.",
    },
    final: {
      pull: "De eerste menstruatie hoeft niet aan te komen als iets dat een meisje al zou moeten beheersen. We kunnen haar voorbereiden.",
      title: "Bereid haar voor — rustig, eerlijk, samen.",
      rows: [
        "Schoolworkshop in twee sessies · 2 × 75 minuten",
        "Voor meisjes van 10–14 jaar die nog niet of pas sinds kort menstrueren",
        "Groepen van 15–25 meisjes · gegeven op jullie school · in het Engels",
        "Schiedam · Rotterdam · Den Haag en omgeving",
        "€550 all-inclusive",
      ],
      cta: "Vraag naar een schoolworkshop",
      secondary: "Eerst een vraag? Neem contact op",
    },
  },
};
